import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import { endOfQuarter, endOfYear } from 'date-fns';
import { AppView, Indicator, rem, SettingsItemNavigate } from 'design-system';
import Button, { HEIGHT } from 'design-system/Button';
import { IconBank, IconBoldCalendar } from 'design-system/icons';
import Text from 'design-system/Text';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import IconSpace from 'design-system/icons/IconSpace';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { formatAmount, formatPeriod } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectFetchingMySpaces, selectHasSpaces, selectSelectedSpaceData, selectSpaces, } from 'features/spaces/reducers/selectors';
import Modal from 'utils/packages/Modal';
import useAnalyticsFilter from 'design-system/analyticsFilter';
import { getFromFormatted } from 'features/analytics/components/AnalyticsControls';
import { toUtcDayISO } from 'features/analytics/utils/utils';
import { selectAccountById } from 'reducers/selectors';
import { getSpaceInfo } from 'features/spaces/actions';
import Links from 'utils/links';
import { Tier } from 'features/premium/entities';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { SelectSpacesModalContent } from './components/SelectSpacesModalContent';
import { isWeb } from '../../constants';
import { SelectAccountsModalContent } from './components/SelectAccountsModalContent';
import { exportData, getExportTransactions, setIsFetchingSpacesInfo, setLastSpacesInfoFetchTimestamp, } from './actions';
import { useExportDataState } from './hooks/useExportDataState';
import { isWithinExportDays } from './utils';
const ExportDataScreenContent = ({ navigation, route }) => {
    const { accountIds, from: initFrom, to: initTo } = route.params || {};
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const transactions = useAppSelector((state) => state.exportData.transactions);
    const hasSpaces = useAppSelector(selectHasSpaces);
    const singleAccount = useAppSelector((state) => {
        if (accountIds?.length !== 1)
            return undefined;
        return selectAccountById(state, accountIds[0]);
    });
    const currentSpace = useAppSelector(selectSelectedSpaceData);
    const { isLoadingState, hiddenAccounts, selectedSpaces, selectedAccounts, filteredSpacesWithAccounts, onSaveAccounts, onSaveSpaces, spacesToShow, selectedAccountsMap, accountsToShow, } = useExportDataState(accountIds);
    const scrollValue = useSharedValue(0);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollValue.value = handle.contentOffset.y;
    }, []);
    const onPressDisabledSpace = useCallback((item, isOwner) => {
        Modal.hide(() => {
            if (isOwner) {
                navigation.navigateDeprecated('EmmaProModal', {
                    benefitId: 'spaces',
                    tier: Tier.ultimate,
                    isTierRequired: true,
                });
            }
            else {
                navigation.navigateDeprecated('SpaceUnavailable', { id: item.id });
            }
        });
    }, []);
    const [openingModal, setOpeningModal] = useState();
    const onPressSpaces = useCallback(() => {
        if (isLoadingState) {
            setOpeningModal('spaces');
            return;
        }
        Modal.show(<SelectSpacesModalContent spacesToShow={spacesToShow} hiddenAccounts={hiddenAccounts} selectedSpaces={selectedSpaces} onSave={onSaveSpaces} onScroll={scrollHandler} onPressDisabled={onPressDisabledSpace}/>, {
            containerStyle: styles.modal,
            title: 'Select spaces',
            scrollable: { scrollValue },
        });
    }, [
        isLoadingState,
        spacesToShow,
        hiddenAccounts,
        selectedSpaces,
        onSaveSpaces,
        scrollHandler,
        onPressDisabledSpace,
        scrollValue,
    ]);
    const selectedAccountsText = useMemo(() => {
        if (selectedAccounts.length === 1) {
            return selectedAccounts[0].name;
        }
        if (!selectedAccounts.length && singleAccount) {
            return singleAccount.name;
        }
        if (!selectedAccounts.length ||
            selectedAccounts.length === accountsToShow.length) {
            return 'All';
        }
        return `${selectedAccounts.length} accounts`;
    }, [accountsToShow.length, selectedAccounts, singleAccount]);
    const onPressAccounts = useCallback(() => {
        if (isLoadingState) {
            setOpeningModal('accounts');
            return;
        }
        Modal.show(<SelectAccountsModalContent spacesToShow={filteredSpacesWithAccounts} onScroll={scrollHandler} selectedAccounts={selectedAccounts} onSave={onSaveAccounts}/>, {
            containerStyle: styles.modal,
            title: 'Select accounts',
            scrollable: { scrollValue },
        });
    }, [
        filteredSpacesWithAccounts,
        isLoadingState,
        onSaveAccounts,
        scrollHandler,
        scrollValue,
        selectedAccounts,
    ]);
    useEffect(() => {
        if (isLoadingState || !openingModal)
            return;
        if (openingModal === 'spaces') {
            onPressSpaces();
        }
        else if (openingModal === 'accounts') {
            onPressAccounts();
        }
        setOpeningModal(undefined);
    }, [isLoadingState, onPressAccounts, onPressSpaces, openingModal]);
    const [selectedPeriod, setSelectedPeriod] = useState(() => {
        if (initFrom || initTo) {
            return {
                type: 'custom',
                from: initFrom ? new Date(initFrom) : new Date(2015, 0, 1),
                to: initTo ? new Date(initTo) : new Date(),
            };
        }
        return undefined;
    });
    const [show, hide] = useAnalyticsFilter();
    const onSavePeriod = useCallback((period) => {
        setSelectedPeriod(period);
        hide();
    }, [hide]);
    const onPressPeriod = useCallback(() => {
        if (isLoadingState) {
            setOpeningModal(undefined);
        }
        const defaults = selectedPeriod
            ? {
                weeklyRange: selectedPeriod.type === 'week'
                    ? [selectedPeriod.from, selectedPeriod.to]
                    : undefined,
                monthly: selectedPeriod.type === 'month'
                    ? {
                        currentMonth: selectedPeriod.month - 1,
                        currentYear: selectedPeriod.year,
                    }
                    : undefined,
                quarterly: selectedPeriod.type === 'quarter'
                    ? {
                        currentQuarter: selectedPeriod.quarter,
                        currentYear: selectedPeriod.year,
                    }
                    : undefined,
                yearly: selectedPeriod.type === 'year' ? selectedPeriod.year : undefined,
                custom: selectedPeriod.type === 'custom'
                    ? [selectedPeriod.from, selectedPeriod.to]
                    : undefined,
            }
            : undefined;
        show(onSavePeriod, selectedPeriod ? () => onSavePeriod(undefined) : undefined, defaults);
    }, [isLoadingState, selectedPeriod, show, onSavePeriod]);
    const marginBottom = useMarginBottom('marginBottom', HEIGHT + rem(8), styles.transactionsCount);
    const selectedSpacesText = useMemo(() => {
        if (selectedSpaces.length === 1) {
            if (selectedSpaces[0].isDefault) {
                return 'Private space';
            }
            return selectedSpaces[0].name;
        }
        if (!selectedSpaces.length && accountIds && currentSpace) {
            if (currentSpace.isDefault) {
                return 'Private space';
            }
            return currentSpace.name;
        }
        if (!selectedSpaces.length ||
            selectedSpaces.length === spacesToShow.length) {
            return 'All';
        }
        return `${selectedSpaces.length} spaces`;
    }, [accountIds, currentSpace, selectedSpaces, spacesToShow.length]);
    const { from, to, periodText } = useMemo(() => {
        if (!selectedPeriod) {
            return { periodText: 'All', from: toUtcDayISO(new Date(2015, 0, 1)) };
        }
        const [from, to] = (() => {
            let to;
            switch (selectedPeriod.type) {
                case 'week':
                    to = toUtcDayISO(selectedPeriod.to);
                    break;
                case 'month':
                    to = toUtcDayISO(new Date(selectedPeriod.year, selectedPeriod.month, 0));
                    break;
                case 'quarter':
                    to = toUtcDayISO(endOfQuarter(getFromFormatted(selectedPeriod)));
                    break;
                case 'year':
                    to = toUtcDayISO(endOfYear(getFromFormatted(selectedPeriod)));
                    break;
                case 'custom':
                    to = toUtcDayISO(selectedPeriod.to);
                    break;
                default:
                    break;
            }
            return [toUtcDayISO(getFromFormatted(selectedPeriod)), to];
        })();
        return {
            from,
            to,
            periodText: formatPeriod({ from, to }, undefined, undefined, undefined, true, true),
        };
    }, [selectedPeriod]);
    const transactionsCount = useMemo(() => {
        const filtered = transactions.filter(({ accountId, date }) => {
            if (!isLoadingState && !selectedAccountsMap[accountId]) {
                return false;
            }
            return isWithinExportDays(date, from, to);
        });
        return filtered.length;
    }, [transactions, isLoadingState, selectedAccountsMap, from, to]);
    const onExport = useRequiredBenefitCallback(useCallback(async () => {
        let accountIdsBody = selectedAccounts
            .map(({ id }) => id)
            .filter((id) => typeof id === 'number');
        if (!selectedAccounts.length && accountIds) {
            accountIdsBody = accountIds;
        }
        const res = await fetch(exportData({
            from,
            to,
            accountIds: accountIdsBody,
        }));
        if (!res?.link)
            return;
        Links.openLink(res.link);
        if (isWeb) {
            navigation.navigateDeprecated('Success', {
                title: 'Your data has been successfully downloaded to your device',
                subTitle: '',
                onContinue: () => {
                    navigation.pop(2);
                    return true;
                },
                buttonText: 'Close',
            });
        }
        else {
            navigation.pop();
        }
    }, [selectedAccounts, accountIds, from, to]), 'export');
    const handleExport = useCallback(() => {
        if (isLoadingState) {
            setOpeningModal(undefined);
        }
        onExport();
    }, [isLoadingState, onExport]);
    return (<AppView avoidHeader>
      <ScreenInfoHeader title="Export your data"/>
      <View style={styles.flex}>
        {!!hasSpaces && (<TouchableBounceCard onPress={onPressSpaces} onDark noMarginTop>
            <View pointerEvents="none" style={styles.card}>
              <SettingsItemNavigate description="Spaces" title={selectedSpacesText} icon={<IconSpace gradientKey="lightPurple"/>} isFetching={Boolean(isLoadingState && openingModal === 'spaces')}/>
            </View>
          </TouchableBounceCard>)}
        <TouchableBounceCard onPress={onPressAccounts} onDark noMarginTop>
          <View pointerEvents="none" style={styles.card}>
            <SettingsItemNavigate description="Accounts" title={selectedAccountsText} icon={<IconBank gradientKey="darkPink"/>} isFetching={Boolean(isLoadingState && openingModal === 'accounts')}/>
          </View>
        </TouchableBounceCard>
        <TouchableBounceCard onPress={onPressPeriod} onDark noMarginTop>
          <View pointerEvents="none" style={styles.card}>
            <SettingsItemNavigate description="Period" title={periodText} icon={<IconBoldCalendar gradientKey="darkPurple"/>}/>
          </View>
        </TouchableBounceCard>
      </View>
      {!!transactions.length &&
            (transactionsCount ? (<Text TextThin-16 Secondary centered style={marginBottom}>
            You are about to export{' '}
            <Text Text-16 Secondary>
              {formatAmount(transactionsCount)}{' '}
            </Text>
            transactions
          </Text>) : (<Text TextThin-16 Red centered style={marginBottom}>
            No transactions found matching filters
          </Text>))}
      <Button brand floating title="Export" disabled={!!transactions.length && !transactionsCount} onPress={handleExport} isFetching={isFetching}/>
    </AppView>);
};
const ExportDataScreen = (props) => {
    const { navigation } = props;
    const colors = useColors();
    const fetchingMySpaces = useAppSelector(selectFetchingMySpaces);
    const spaces = useAppSelector(selectSpaces);
    const lastSpacesInfoFetchTimestamp = useAppSelector((store) => store.exportData.lastSpacesInfoFetchTimestamp);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Export your data',
            headerTitle: '',
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.EXPORT_DATA}/>),
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const dispatch = useAppDispatch();
    const lastTransactionsFetchTimestamp = useAppSelector((state) => state.exportData.lastTransactionsFetchTimestamp);
    useEffect(() => {
        if (!lastTransactionsFetchTimestamp ||
            lastTransactionsFetchTimestamp < Date.now() - 1000 * 60) {
            dispatch(getExportTransactions());
        }
    }, [lastTransactionsFetchTimestamp]);
    const needsRefetch = useMemo(() => !lastSpacesInfoFetchTimestamp ||
        lastSpacesInfoFetchTimestamp < Date.now() - 1000 * 60 * 5, [lastSpacesInfoFetchTimestamp]);
    useEffect(() => {
        if (fetchingMySpaces || !needsRefetch)
            return;
        const fetchSpacesInfo = async () => {
            dispatch(setIsFetchingSpacesInfo(true));
            try {
                await Promise.all(spaces.map(({ id }) => dispatch(getSpaceInfo(id))));
                dispatch(setLastSpacesInfoFetchTimestamp(Date.now()));
            }
            catch {
                //
            }
            dispatch(setIsFetchingSpacesInfo(false));
        };
        fetchSpacesInfo();
    }, [fetchingMySpaces, spaces, needsRefetch]);
    if (fetchingMySpaces) {
        return <Indicator dark/>;
    }
    return <ExportDataScreenContent {...props}/>;
};
const styles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    flex: {
        flex: 1,
    },
    transactionsCount: {
        marginHorizontal: rem(16),
    },
    card: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
});
export default ExportDataScreen;
